/**
 * Real background blur/replacement for the camera track.
 *
 * Compositing pipeline (per frame, on a canvas captured as the output track):
 *  1. draw the raw video frame (base);
 *  2. "blur": draw a second copy of the frame with a canvas blur filter on top;
 *     "office": draw the generated office backdrop instead;
 *  3. repaint the person region with the sharp raw frame, masked by the latest
 *     segmentation mask softened through a feather pass (person pixels opaque,
 *     background transparent, smooth gradient between) - so the person stays
 *     sharp with soft edges while the background is blurred/replaced.
 *
 * The feather pass draws the raw model mask into a quarter-resolution canvas
 * with `ctx.filter = blur(...)` (radius scaled down by the same factor), then
 * upscales the softened result to the output size: the compositing boundary
 * becomes a gradient instead of a jagged cutout. At the "high" quality level
 * the current and previous softened masks blend 50/50 to damp edge flicker.
 *
 * The quality level ("low" for weak machines, "high" for powerful ones) sizes
 * the processing canvas, the capture rate, the feather radius and the blur
 * radius; "auto" resolves the level once at start from a short inference
 * benchmark (see {@link BENCHMARK_PASSES}). The benchmark never exceeds 10
 * frames.
 *
 * A whole-frame color filter (videoEffects.ts presets) applies on top of any
 * mode as one extra self-draw pass over the finished frame: the composite is
 * snapshotted and redrawn onto the output canvas through `ctx.filter`, so
 * person and background are tinted together. The pass costs one full-frame
 * blit; the raw mode ("none" background) also runs when a filter is active -
 * the documented cost of that combination is the composite pass itself, while
 * segmentation is skipped (a whole-frame filter needs no person mask).
 *
 * COMPOSITE BACKENDS: the composite stage (background layer under the sharp,
 * mask-cut person) runs on a GPU path when the browser provides WebGL2
 * (webglComposite.ts): the raw frame uploads as a texture, the background is a
 * separable Gaussian blurred at FULL output resolution in two shader passes,
 * and the cutout samples the low-res mask with bilinear filtering plus a
 * smoothstep feather ramp - sharp subject, genuinely blurred background, one
 * composite draw per frame, no quarter-res softness. The 2D path described
 * below stays as the fallback (no WebGL2, GPU init failure, dev override
 * `window.__vcFxBackend='canvas2d'`); the injected {@link ProcessorDeps.createCompositeRenderer}
 * dep picks per pipeline rebuild and jsdom tests script it directly. The dev
 * bridge `window.__vcFxPerf()` (exposeFxPerfForTests) reports the active
 * backend and the composite stage's ms/frame EMA.
 *
 * A face mask (faceMasks.ts presets: glasses/mustache/hat/crown) draws on top of
 * the finished composite too, BEFORE the color-filter pass (the filter tints the
 * mask together with everything else). The mask is positioned by local MediaPipe
 * FaceLandmarker inference: anchors come from the canonical eye line / nose tip /
 * face-oval top, landmarks are EMA-smoothed, the mask fades out after 0.5 s
 * without a face. Masks work in EVERY background mode: in the raw mode the tick
 * skips segmentation entirely (a mask needs landmarks only) and routes the frame
 * through the composite for the mask draw; when a background mode combines with
 * a mask both models run per frame, and on the "low" tier landmark inference is
 * throttled to every other frame (the EMA pose covers the skipped frames). A
 * landmark-engine failure in the raw+mask mode degrades the processor to the raw
 * track (the pipeline would run for nothing); on an active background pipeline
 * the failure only drops the mask - the treatment keeps running.
 *
 * PERF CONTRACT (why the camera does not lag under effects):
 *  - INFERENCE never runs at the session resolution: the tick downsamples the raw
 *    frame onto a small internal canvas ({@link INFERENCE_LONG_SIDE} long side) and
 *    both engines read THAT - the models rescale to their own input sizes anyway,
 *    masks/landmarks are resolution-insensitive, and the per-frame texture
 *    upload/readback cost drops an order of magnitude at 1080p captures.
 *  - THE PUMP NEVER WAITS FOR INFERENCE: compositing runs every tick off the last
 *    fresh results; a new inference runs only when enough time has passed since
 *    the previous one ({@link INFERENCE_BACKOFF} x its own measured duration), so
 *    a slow engine drops its own cadence instead of stalling every frame. Results
 *    are consumed synchronously - there is no queue to grow.
 *  - The blur-mode background copy is itself blurred at QUARTER resolution (the
 *    feather pattern: radius scaled down by {@link FEATHER_DOWNSCALE}, then the
 *    softened canvas upscales) - a `ctx.filter` pass at 1/16 of the pixels with
 *    the same visual spread.
 *
 * The finished composite blits once per frame onto the PUBLISHED canvas, and that
 * canvas is captured at the quality's fps (24 on high) and delivered as the `track`
 * of the result; the room publishes that track instead of a fresh camera capture.
 * The published size is chosen ONCE per capture session (a processor generation)
 * by {@link publishSize}: min(capture dims, session publish cap), even-rounded. The
 * cap is the session tier's cap from publishQuality.ts (low 640x360, medium 1280x720,
 * high 1920x1080) - the SAME cap that constrained the raw capture, so a raw<->canvas
 * effects swap encodes both sides at the same size.
 * Two invariants drive the policy:
 *  - STABILITY across in-session effects swaps: livekit-client recomputes the
 *    sender's simulcast encodings whenever a replaced track changes dimensions
 *    (LocalVideoTrack.onSenderTrackSwapped -> refreshSenderEncodings), and that
 *    mid-session reconfiguration desyncs the SFU's layer bookkeeping - every
 *    subscriber whose adaptive-stream view sits above the lowest layer is re-mapped
 *    to a smaller layer with a multi-second keyframe gap (observed as a frozen
 *    remote tile after an in-call effects swap). Every swap that restarts the
 *    processor from the same capture at the same session cap yields the SAME
 *    published size, so replaceTrack keeps the sender encodings and RTP flows
 *    uninterrupted.
 *  - AFFORDABILITY for the tier: the session tier already right-sizes the capture
 *    (and with it the encode budget) to the machine; the effects quality level only
 *    sizes the PROCESSING canvas, never the published size, so an effects swap can
 *    no longer re-negotiate the sender mid-call.
 * A mid-session capture dimension change KEEPS the current published size unless
 * it no longer fits the new capture - see {@link publishSize}. Besides the
 * publish cap, the tier caps govern processing cost (fps, feather/blur radii,
 * smoothing), never per-swap re-sizing.
 *
 * PRIVACY: video frames never leave the page. This module is deliberately free
 * of any network surface (no fetch, no URLs); the wasm runtime and the model
 * load as same-origin static assets from /wasm and /models (see
 * segmentationEngine.ts, guarded by a unit test).
 *
 * Everything browser-specific (canvas, captureStream, MediaPipe engine, frame
 * scheduling) is injected via {@link ProcessorDeps}, so the decision/lifecycle
 * logic runs under jsdom with recording fakes.
 */

import { blurRadiusPx, VIDEO_FILTER_CSS } from './videoEffects';
import type { BlurStrength, VideoFilter } from './videoEffects';
import type { FaceLandmarkList, FaceMaskId, MaskSpriteSet } from './faceMasks';
import type {
  CompositeBackground,
  CompositeRenderer,
  FxRenderPerfSnapshot,
} from './compositeRenderer';
import {
  CANONICAL_EYE_DISTANCE,
  facePoseFromLandmarks,
  landmarkSmoothingAlpha,
  maskAlpha,
  maskDefinitionOf,
  shouldDetectLandmarks,
  smoothFace,
} from './faceMasks';
import type { SmoothedFace } from './faceMasks';

/** Background treatment chosen on the prejoin screen (persisted in device prefs). */
export type BackgroundMode = 'none' | 'blur' | 'office';

/** A background mode that produces a treated background (kept for readability at use sites). */
export type ActiveBackgroundMode = Exclude<BackgroundMode, 'none'>;

/**
 * Quality setting chosen on the prejoin screen: "auto" resolves the level once
 * at processor start from a quick benchmark, "low" and "high" pin it.
 */
export type BackgroundQualitySetting = 'auto' | 'low' | 'high';

/** Quality level the processor actually runs at (the "auto" resolution outcome). */
export type ResolvedBackgroundQuality = 'low' | 'high';

/** Why a start fell back to the raw camera track. */
export type BackgroundFallbackReason = 'unavailable' | 'too_slow';

/**
 * Category-mask byte that marks a PERSON pixel in the selfie_segmenter output.
 * VERIFIED LIVE against the committed model (and its getLabels() = ["selfie"]): the model
 * has a single category, person pixels carry byte 0 (the "selfie" category) and background
 * pixels carry 255 (unlabeled) - the model card's "person at index 1" refers to the label
 * index, not the uint8 mask byte.
 */
export const PERSON_CATEGORY = 0;

/** Alpha value for a person pixel in the converted mask. */
export const PERSON_MASK_VALUE = 255;

/** Output canvas capture rate (fps) for the "high" quality level. */
export const OUTPUT_FPS = 24;

/**
 * Tuning parameters per resolved quality level. "low" targets weak machines
 * (small processing canvas, 15 fps, modest feather/blur, no temporal smoothing);
 * "high" targets powerful ones (up to 720p but never above the camera's native
 * resolution, 24 fps, wide feather/blur, temporal mask smoothing). These caps
 * size the PROCESSING pipeline only - the published canvas follows
 * {@link PUBLISH_CAPS} / {@link publishSize}.
 */
export interface BackgroundQualityParams {
  /** Processing canvas width cap (never upscales past the camera native). */
  readonly maxWidth: number;
  /** Processing canvas height cap. */
  readonly maxHeight: number;
  /** Output canvas capture rate (fps). */
  readonly fps: number;
  /** Mask edge feather radius at output resolution (px). */
  readonly featherPx: number;
  /** Canvas blur radius of the background copy in "blur" mode (px). */
  readonly blurPx: number;
  /** Whether the previous frame's mask blends into the current one (50/50). */
  readonly temporalSmoothing: boolean;
}

/** Per-level parameters (the director's design table). */
export const BACKGROUND_QUALITY_PARAMS: Readonly<
  Record<ResolvedBackgroundQuality, BackgroundQualityParams>
> = {
  low: {
    maxWidth: 640,
    maxHeight: 360,
    fps: 15,
    featherPx: 6,
    blurPx: 10,
    temporalSmoothing: false,
  },
  high: {
    maxWidth: 1280,
    maxHeight: 720,
    fps: OUTPUT_FPS,
    featherPx: 16,
    blurPx: 16,
    temporalSmoothing: true,
  },
};

/** Segmentation passes measured by the "auto" benchmark (plus one uncounted warm-up). */
export const BENCHMARK_PASSES = 8;

/** Benchmark average under which the machine resolves to the "high" level (ms). */
export const BENCHMARK_HIGH_AVG_MS = 25;

/**
 * Benchmark average over which the machine is too slow for effects at all (ms);
 * between {@link BENCHMARK_HIGH_AVG_MS} and this value the effects still run,
 * at the "low" parameters.
 */
export const BENCHMARK_TOO_SLOW_AVG_MS = 120;

/** Feather canvas downscale factor relative to the output canvas (quarter size). */
export const FEATHER_DOWNSCALE = 4;

/**
 * Long side of the internal inference input canvas. The segmenter and landmarker
 * models rescale every input to their own fixed sizes (256/192), so feeding the
 * session-resolution frame only pays texture upload/readback for nothing: the
 * tick draws the raw frame onto this small canvas (aspect preserved, never
 * upscaled) and both engines read it. Masks are visually insensitive to the
 * resulting sharpness; landmark coordinates are normalized, so the pose math is
 * unchanged.
 */
export const INFERENCE_LONG_SIDE = 320;

/**
 * Pacing of inference against the compositing pump: after a pass measured at N ms,
 * the next one waits at least BACKOFF x N ms (an engine that costs the pump its
 * whole frame budget drops its own cadence instead of stalling every tick). The
 * composite always renders the last fresh result, so published fps tracks the
 * pump, not the engine. Fast engines (measured ~0 ms) keep running every tick.
 */
export const INFERENCE_BACKOFF = 3;

/** Canvas blur filter string for a radius in px. */
export function blurFilter(px: number): string {
  return `blur(${px}px)`;
}

/** How long to wait for the hidden video to deliver its first frame dimensions. */
export const VIDEO_READY_TIMEOUT_MS = 5000;

/** Segmentation engine: produces a person alpha mask for one video frame. */
export interface SegmentationEngine {
  /**
   * Segments the frame's current content; `onMask` receives the mask (person =
   * {@link PERSON_MASK_VALUE}, background = 0) in model resolution. May be called
   * synchronously. The frame is whatever the processor reads inference from (the
   * hidden video or the small internal inference canvas). Throws on runtime
   * failure, which degrades the processor.
   */
  segment(
    frame: HTMLVideoElement | HTMLCanvasElement,
    timestampMs: number,
    onMask: (mask: PersonMask) => void,
  ): void;
  /** Releases engine resources; safe to call once. */
  close(): void;
}

/**
 * Face-landmark engine: detects at most one face per frame for mask anchoring.
 * Returns the landmark list of the first face in normalized frame coordinates,
 * or null when no face is visible (the mask fades out - see faceMasks.ts).
 * The frame follows the same inference-input rule as {@link SegmentationEngine}.
 * Throws on runtime failure, which degrades the processor.
 */
export interface FaceLandmarkEngine {
  detect(frame: HTMLVideoElement | HTMLCanvasElement, timestampMs: number): FaceLandmarkList | null;
  /** Releases engine resources; safe to call once. */
  close(): void;
}

/** One segmentation result: person mask in model resolution, 255 = person. */
export interface PersonMask {
  readonly data: Uint8Array;
  readonly width: number;
  readonly height: number;
}

/**
 * Browser specifics the processor needs. The production set lives in
 * segmentationEngine.ts (lazy-imported so jsdom tests never touch it).
 */
export interface ProcessorDeps {
  /** Creates the segmentation engine; rejection falls back to the raw track. */
  readonly createEngine: () => Promise<SegmentationEngine>;
  /** Creates the face-landmark engine (lazy: only when a mask activates). */
  readonly createLandmarkEngine: () => Promise<FaceLandmarkEngine>;
  /** Rasterizes the mask sprites once (browser-only Path2D work); null = no masks. */
  readonly createMaskSprites: () => MaskSpriteSet | null;
  /** Creates the hidden video element that consumes the camera track. */
  readonly createVideo: () => HTMLVideoElement;
  /** Creates a canvas of the given size with a usable 2d context. */
  readonly createCanvas: (width: number, height: number) => HTMLCanvasElement;
  /**
   * Creates the GPU composite renderer for the given output size, or null when
   * WebGL2 is unavailable (the processor then keeps the 2D composite path).
   * Called once per pipeline rebuild; the processor owns (disposes) the result.
   */
  readonly createCompositeRenderer: (width: number, height: number) => CompositeRenderer | null;
  /** Renders the generated office backdrop at the given size onto a fresh canvas. */
  readonly drawOfficeBackdrop: (width: number, height: number) => HTMLCanvasElement;
  /** Captures a canvas as a video track at the quality level's fps. */
  readonly captureStream: (canvas: HTMLCanvasElement, fps: number) => MediaStreamTrack;
  /** Wraps a track into a video-only MediaStream (preview convenience). */
  readonly createStream: (track: MediaStreamTrack) => MediaStream;
  /** Wraps mask bytes into an ImageData for putImageData. */
  readonly createImageData: (
    data: Uint8ClampedArray<ArrayBuffer>,
    width: number,
    height: number,
  ) => ImageData;
  /**
   * Schedules the next frame callback for the video (requestVideoFrameCallback
   * where available, requestAnimationFrame otherwise); returns the cancel.
   */
  readonly scheduleFrame: (video: HTMLVideoElement, callback: () => void) => () => void;
  /** Monotonic-ish clock for segmentation timestamps (must strictly increase). */
  readonly now: () => number;
}

/** A running processor delivering the composited track. `processed` discriminates it. */
export interface BackgroundResult {
  readonly processed: true;
  /** The composited canvas video track (preview and publish). */
  readonly track: MediaStreamTrack;
  /** Video-only stream carrying {@link track}, ready for a `<video>.srcObject`. */
  readonly stream: MediaStream;
  /** The quality level this processor resolved to (the benchmark outcome for "auto"). */
  readonly resolvedQuality: ResolvedBackgroundQuality;
  /** The RAW camera track feeding the pipeline (the in-call machine rebuilds from it). */
  readonly cameraTrack: MediaStreamTrack;
  /** Switches the treatment live (blur <-> office <-> raw pass-through). */
  setMode(mode: BackgroundMode): void;
  /** Switches the blur strength live (radius recomputed from the level's base). */
  setBlurStrength(strength: BlurStrength): void;
  /** Switches the whole-frame color filter live. */
  setFilter(filter: VideoFilter): void;
  /** Switches the face mask live (a first activation lazily starts the landmark engine). */
  setMask(mask: FaceMaskId): void;
  /** Transfers disposal ownership (join handover): stop() becomes a no-op. */
  claimOwnership(): void;
  /** Releases everything except the input camera track (its owner stops it). */
  stop(): void;
  /**
   * Machine-side release for a live switch: releases everything EXCEPT the input
   * camera track regardless of claimOwnership - the switching machine (the current
   * owner) replaces this processor while the next generation keeps the capture alive.
   */
  releaseExceptCapture(): void;
  /**
   * Handover disposal: releases everything including the input camera track,
   * regardless of claimOwnership. The room page calls it on every leave path.
   */
  dispose(): void;
}

/** Degraded outcome: the raw camera track is delivered unchanged. */
export interface BackgroundFallback {
  readonly processed: false;
  /** Why the processor is not running: engine failure or a too-slow machine. */
  readonly reason: BackgroundFallbackReason;
  readonly track: MediaStreamTrack;
  readonly stream: MediaStream;
  /** The raw camera track (identical to {@link track} on the fallback). */
  readonly cameraTrack: MediaStreamTrack;
  setMode(mode: BackgroundMode): void;
  setBlurStrength(strength: BlurStrength): void;
  setFilter(filter: VideoFilter): void;
  setMask(mask: FaceMaskId): void;
  claimOwnership(): void;
  stop(): void;
  releaseExceptCapture(): void;
  dispose(): void;
}

export type BackgroundOutcome = BackgroundResult | BackgroundFallback;

export interface StartOptions {
  /** Defaults to the production browser deps (lazily imported). */
  readonly deps?: ProcessorDeps | undefined;
  /**
   * Called once when a running processor degrades at runtime (segmentation
   * error): the output track is dead, the caller must fall back to raw.
   */
  readonly onFallback?: (() => void) | undefined;
  /**
   * Quality level for the pipeline; "auto" resolves it once at start from a
   * short inference benchmark. Defaults to "high" (the v1 single-level behavior).
   */
  readonly quality?: BackgroundQualitySetting | undefined;
  /**
   * The SESSION publish cap for the output track: min(capture dims, cap) sizes the
   * published canvas (see {@link publishSize}). The caller passes the session tier's
   * cap (PUBLISH_TIER_CAPS in publishQuality.ts) - the same cap that constrained the
   * raw capture - so a raw<->canvas effects swap never changes the published
   * dimensions. `null` publishes at the capture's own size.
   */
  readonly publishCap: { readonly width: number; readonly height: number } | null;
  /** Background blur strength; defaults to "medium" (the pre-S2 look). */
  readonly blurStrength?: BlurStrength | undefined;
  /** Whole-frame color filter; defaults to "none". */
  readonly filter?: VideoFilter | undefined;
  /** Face mask overlay; defaults to "none". */
  readonly mask?: FaceMaskId | undefined;
}

/** Internal marker: the "auto" benchmark measured a machine too slow for effects. */
class BackgroundTooSlowError extends Error {
  constructor(avgMs: number) {
    super(`segmentation benchmark average ${avgMs}ms exceeds the effects threshold`);
    this.name = 'BackgroundTooSlowError';
  }
}

/**
 * Starts the processor for a camera track. Never throws: failures resolve to a fallback.
 * The mode may be "none" when a whole-frame color filter is active: the frame then routes
 * through the composite without segmentation (the documented raw+filter cost). The
 * SESSION publish cap is mandatory: the caller (prejoin preview or the in-call machine)
 * states the tier cap the raw capture was constrained with, so the canvas publishes at
 * exactly the raw track's dimensions and the effects swap stays dimension-stable.
 */
export async function startBackgroundProcessor(
  cameraTrack: MediaStreamTrack,
  mode: BackgroundMode,
  options: StartOptions,
): Promise<BackgroundOutcome> {
  const deps = options.deps ?? (await loadBrowserDeps());
  try {
    return await new BackgroundProcessor(
      cameraTrack,
      mode,
      options.quality ?? 'high',
      options.publishCap,
      options.blurStrength ?? 'medium',
      options.filter ?? 'none',
      options.mask ?? 'none',
      deps,
      options.onFallback,
    ).start();
  } catch (error: unknown) {
    const reason: BackgroundFallbackReason =
      error instanceof BackgroundTooSlowError ? 'too_slow' : 'unavailable';
    return rawFallback(cameraTrack, deps, reason);
  }
}

/** The raw-track outcome: preview/publish continue exactly as with mode "none". */
export function rawFallback(
  cameraTrack: MediaStreamTrack,
  deps: ProcessorDeps,
  reason: BackgroundFallbackReason = 'unavailable',
): BackgroundFallback {
  const track = cameraTrack;
  const stream = deps.createStream(track);
  return {
    processed: false,
    reason,
    track,
    cameraTrack,
    stream,
    setMode: () => undefined,
    setBlurStrength: () => undefined,
    setFilter: () => undefined,
    setMask: () => undefined,
    claimOwnership: () => undefined,
    stop: () => undefined,
    releaseExceptCapture: () => undefined,
    dispose: () => undefined,
  };
}

/**
 * True when the processor should run: a processing mode or a face mask is chosen
 * and a camera track exists. "none" everywhere and camera-off both keep the raw path.
 */
export function shouldProcessBackground(
  mode: BackgroundMode,
  cameraTrack: MediaStreamTrack | null | undefined,
  mask: FaceMaskId = 'none',
): boolean {
  return (mode !== 'none' || mask !== 'none') && cameraTrack !== null && cameraTrack !== undefined;
}

/**
 * Output size for a native frame: never upscaled, scaled down to fit the
 * quality's caps preserving aspect, rounded to even values (encoder-friendly).
 */
export function fittedSize(
  nativeWidth: number,
  nativeHeight: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  const scale = Math.min(maxWidth / nativeWidth, maxHeight / nativeHeight, 1);
  return {
    width: Math.max(2, 2 * Math.floor((nativeWidth * scale) / 2)),
    height: Math.max(2, 2 * Math.floor((nativeHeight * scale) / 2)),
  };
}

/**
 * The published size for a capture, chosen ONCE per capture session (one
 * processor generation): min(capture dims, session publish cap), even-rounded
 * (encoder-friendly). The result is a pure function of (capture, cap), so every
 * effects swap that restarts the processor from the same capture at the same
 * cap reproduces the SAME size - replaceTrack then keeps the sender's
 * simulcast encodings instead of renegotiating them mid-session (module header).
 *
 * The cap is the SESSION publish cap (PUBLISH_TIER_CAPS[publishTier], resolved once
 * per join in publishQuality.ts) - the SAME cap that constrained the raw capture.
 * That is what makes the raw<->canvas effects swap dimension-stable: the raw track
 * and the canvas are encoded at the same size, so the swap never rewrites the
 * sender's encodings. `null` means uncapped (publish at the capture's own size).
 *
 * `current` (the running publish canvas's size) carries the mid-session rule: a
 * capture dimension change KEEPS the current published size unless it no longer
 * fits the new capture (the capture shrank below it, or the aspect flipped).
 * Re-encoding an unchanged size would only reset the sender for nothing; an
 * oversized canvas would upscale a dying capture. `current` is always
 * even-rounded and within the cap by construction (it was produced by this
 * function earlier in the same session at the same cap).
 */
export function publishSize(
  captureWidth: number,
  captureHeight: number,
  cap: { readonly width: number; readonly height: number } | null,
  current?: { readonly width: number; readonly height: number } | undefined,
): { width: number; height: number } {
  if (current !== undefined && current.width <= captureWidth && current.height <= captureHeight) {
    return { width: current.width, height: current.height };
  }
  const bounds = cap ?? { width: captureWidth, height: captureHeight };
  return fittedSize(captureWidth, captureHeight, bounds.width, bounds.height);
}

class BackgroundProcessor implements BackgroundResult {
  readonly processed = true as const;

  private readonly inputTrack: MediaStreamTrack;
  private readonly deps: ProcessorDeps;
  private readonly onFallback: (() => void) | undefined;
  private readonly qualitySetting: BackgroundQualitySetting;
  /** The session publish cap for the output canvas (see {@link publishSize}). */
  private readonly publishCap: { readonly width: number; readonly height: number } | null;

  private mode: BackgroundMode;
  private blurStrength: BlurStrength;
  private filter: VideoFilter;
  private mask: FaceMaskId;
  private engine: SegmentationEngine | null = null;
  private landmarkEngine: FaceLandmarkEngine | null = null;
  private maskSprites: MaskSpriteSet | null = null;
  /**
   * The GPU composite stage (webglComposite.ts) when the rebuild could create
   * one; null keeps the 2D canvas path. Owned here: disposed on rebuild and
   * teardown, and its render throws degrade the processor like engine errors.
   */
  private renderer: CompositeRenderer | null = null;
  /** Composite stage ms/frame EMA (composite + overlays + publish blit). */
  private compositeMsAvg = 0;
  private compositeFrames = 0;
  /** True while the lazy landmark pipeline is initializing (no duplicate starts). */
  private maskPipelineStarting = false;
  /** True after a landmark/sprite init failure: masks no-op, no retry (documented). */
  private maskFailed = false;
  private smoothedFace: SmoothedFace | null = null;
  /** deps.now() of the last detected face (-Infinity: never, alpha gate keeps the mask hidden). */
  private lastFaceAt = Number.NEGATIVE_INFINITY;
  private tickCount = 0;
  private video: HTMLVideoElement | null = null;
  private cancelFrame: (() => void) | null = null;
  private outputCtx: CanvasRenderingContext2D | null = null;
  private outputCanvas: HTMLCanvasElement | null = null;
  /** Small canvas both engines read inference from (see INFERENCE_LONG_SIDE). */
  private inferenceCanvas: HTMLCanvasElement | null = null;
  private inferenceCtx: CanvasRenderingContext2D | null = null;
  /** Quarter-resolution canvas the blur filter runs on (see FEATHER_DOWNSCALE). */
  private bgCanvas: HTMLCanvasElement | null = null;
  private bgCtx: CanvasRenderingContext2D | null = null;
  private personCanvas: HTMLCanvasElement | null = null;
  private personCtx: CanvasRenderingContext2D | null = null;
  private maskCanvas: HTMLCanvasElement | null = null;
  private maskCtx: CanvasRenderingContext2D | null = null;
  private featherCanvas: HTMLCanvasElement | null = null;
  private featherCtx: CanvasRenderingContext2D | null = null;
  private featheredCanvas: HTMLCanvasElement | null = null;
  private featheredCtx: CanvasRenderingContext2D | null = null;
  private prevFeatherCanvas: HTMLCanvasElement | null = null;
  private prevFeatherCtx: CanvasRenderingContext2D | null = null;
  /**
   * The canvas the output track captures: sized ONCE per capture session by the
   * {@link publishSize} policy - min(capture dims, tier publish cap), even-rounded
   * (the module header carries the stability/affordability rationale). It receives
   * the finished composite once per frame so the published track stays
   * dimension-stable across swaps: a dimension-changing replaceTrack would rewrite
   * the sender's simulcast encodings and remap every large-view subscriber onto a
   * smaller layer with a multi-second keyframe gap.
   */
  private publishCanvas: HTMLCanvasElement | null = null;
  private publishCtx: CanvasRenderingContext2D | null = null;
  private officeCanvas: HTMLCanvasElement | null = null;
  private outputStream: MediaStream | null = null;
  private outputTrack: MediaStreamTrack | null = null;
  private personMask: PersonMask | null = null;
  private width = 0;
  private height = 0;
  private nativeWidth = 0;
  private nativeHeight = 0;
  private stopped = false;
  private claimed = false;
  private degraded = false;
  private lastTimestamp = 0;
  private lastLandmarkTimestamp = 0;
  private params: BackgroundQualityParams | null = null;
  private resolved: ResolvedBackgroundQuality = 'high';
  private prevMaskReady = false;
  /**
   * Adaptive inference pacing ({@link INFERENCE_BACKOFF}): deps.now() when the last
   * segmentation/landmark pass finished and how long it took. The next pass waits
   * until `now - settledAt >= lastMs * INFERENCE_BACKOFF`; a pass measured at ~0 ms
   * (fast machine) never gates. The initial -Infinity opens the first pass at once.
   */
  private segmentSettledAt = Number.NEGATIVE_INFINITY;
  private lastSegmentMs = 0;
  private landmarkSettledAt = Number.NEGATIVE_INFINITY;
  private lastLandmarkMs = 0;
  /** Reused RGBA staging buffer of {@link paintMask} (re-created on a size change). */
  private maskRgba: Uint8ClampedArray<ArrayBuffer> | null = null;

  constructor(
    cameraTrack: MediaStreamTrack,
    mode: BackgroundMode,
    qualitySetting: BackgroundQualitySetting,
    publishCap: { readonly width: number; readonly height: number } | null,
    blurStrength: BlurStrength,
    filter: VideoFilter,
    mask: FaceMaskId,
    deps: ProcessorDeps,
    onFallback: (() => void) | undefined,
  ) {
    this.inputTrack = cameraTrack;
    this.mode = mode;
    this.qualitySetting = qualitySetting;
    this.publishCap = publishCap;
    this.blurStrength = blurStrength;
    this.filter = filter;
    this.mask = mask;
    this.deps = deps;
    this.onFallback = onFallback;
  }

  get track(): MediaStreamTrack {
    if (this.outputTrack === null) {
      throw new Error('background processor has no output track before start() resolves');
    }
    return this.outputTrack;
  }

  get stream(): MediaStream {
    if (this.outputStream === null) {
      throw new Error('background processor has no output stream before start() resolves');
    }
    return this.outputStream;
  }

  get resolvedQuality(): ResolvedBackgroundQuality {
    return this.resolved;
  }

  /** The raw camera track feeding the pipeline (BackgroundResult contract). */
  get cameraTrack(): MediaStreamTrack {
    return this.inputTrack;
  }

  async start(): Promise<BackgroundResult> {
    this.engine = await this.deps.createEngine();
    const video = this.deps.createVideo();
    video.srcObject = this.deps.createStream(this.inputTrack);
    video.muted = true;
    video.playsInline = true;
    await playVideo(video);
    await this.waitForVideoReady(video);
    this.video = video;
    // The inference input canvas exists from here on (the benchmark reads it too;
    // its cost profile is the one the runtime loop will actually see).
    this.ensureInferenceCanvas(video.videoWidth, video.videoHeight);
    if (this.qualitySetting === 'auto') {
      let avgMs: number;
      try {
        avgMs = this.benchmarkInference(video);
      } catch (error: unknown) {
        this.teardown();
        throw error;
      }
      if (avgMs > BENCHMARK_TOO_SLOW_AVG_MS) {
        this.teardown();
        throw new BackgroundTooSlowError(avgMs);
      }
      this.resolved = avgMs <= BENCHMARK_HIGH_AVG_MS ? 'high' : 'low';
    } else {
      this.resolved = this.qualitySetting;
    }
    this.params = BACKGROUND_QUALITY_PARAMS[this.resolved];
    this.rebuildPipeline(video.videoWidth, video.videoHeight);
    if (this.mask !== 'none') {
      this.ensureMaskPipeline();
    }
    this.scheduleNext();
    if (import.meta.env.DEV === true) {
      devPerfReader = (): FxRenderPerfSnapshot => this.perfSnapshot();
    }
    return this;
  }

  setMode(mode: BackgroundMode): void {
    this.mode = mode;
  }

  /** Live blur-strength switch: the radius recomputes from the level's base on the next frame. */
  setBlurStrength(strength: BlurStrength): void {
    this.blurStrength = strength;
  }

  /** Live color-filter switch: applies to (or clears from) the next composited frame. */
  setFilter(filter: VideoFilter): void {
    this.filter = filter;
  }

  /**
   * Live face-mask switch. A first activation lazily starts the landmark engine
   * and rasterizes the sprites (async; until both are ready the mask is simply
   * not drawn yet). Switching back to "none" clears the pose so a re-activation
   * waits for a fresh detection instead of flashing a stale anchor. A landmark
   * init failure degrades the processor only when the pipeline runs solely for
   * the mask (raw+mask); on an active background treatment masks no-op instead.
   */
  setMask(mask: FaceMaskId): void {
    this.mask = mask;
    if (mask === 'none') {
      this.smoothedFace = null;
      this.lastFaceAt = Number.NEGATIVE_INFINITY;
      return;
    }
    this.ensureMaskPipeline();
  }

  /** Lazy landmark pipeline start (once): engine + sprites, then the next tick draws. */
  private ensureMaskPipeline(): void {
    if (this.maskPipelineStarting || this.maskFailed || this.landmarkEngine !== null) {
      return;
    }
    this.maskPipelineStarting = true;
    void (async () => {
      try {
        const [engine, sprites] = await Promise.all([
          this.deps.createLandmarkEngine(),
          Promise.resolve(this.deps.createMaskSprites()),
        ]);
        if (this.stopped) {
          engine.close();
          return;
        }
        if (sprites === null) {
          throw new Error('mask sprites are unavailable');
        }
        this.landmarkEngine = engine;
        this.maskSprites = sprites;
      } catch (error: unknown) {
        this.maskFailed = true;
        if (this.mode === 'none') {
          // The composite runs only for the mask here: without landmarks it is
          // dead weight - degrade to the raw track like a segmentation failure.
          this.degrade();
          return;
        }
        // A background treatment keeps its value on its own; the mask silently
        // no-ops (the same wasm runtime already runs the segmenter there).
        void error;
      } finally {
        this.maskPipelineStarting = false;
      }
    })();
  }

  claimOwnership(): void {
    this.claimed = true;
  }

  stop(): void {
    if (this.claimed) {
      return;
    }
    this.teardown();
  }

  /** Machine-side switch release: the engine and canvases die, the input capture survives. */
  releaseExceptCapture(): void {
    this.teardown();
  }

  /** Handover disposal: always releases everything, including the input camera track. */
  dispose(): void {
    this.teardown();
    this.inputTrack.stop();
  }

  /**
   * One-shot "auto" benchmark: a warm-up pass (first-inference runtime costs are
   * not representative) plus {@link BENCHMARK_PASSES} measured passes over the
   * live video. Cheap by design (9 frames total); the masks are discarded. Runs
   * on the small inference canvas - the cost profile the runtime loop sees. Does
   * NOT touch the runtime pacing state: the gate measures the LOOP's passes.
   */
  private benchmarkInference(video: HTMLVideoElement): number {
    const canvas = this.ensureInferenceCanvas(video.videoWidth, video.videoHeight);
    if (canvas === null) {
      // The video delivered no dimensions (start() waits for them) - treat like a
      // failed engine: the caller falls back to the raw track.
      throw new Error('inference input canvas is unavailable');
    }
    this.segmentFrame(canvas);
    let total = 0;
    for (let pass = 0; pass < BENCHMARK_PASSES; pass += 1) {
      const startedAt = this.deps.now();
      this.segmentFrame(canvas);
      total += this.deps.now() - startedAt;
    }
    return total / BENCHMARK_PASSES;
  }

  /** One segmentation pass whose mask result is discarded (benchmark helper). */
  private segmentFrame(frame: HTMLVideoElement | HTMLCanvasElement): void {
    const engine = this.engine;
    if (engine === null) {
      return;
    }
    const timestamp = Math.max(Math.floor(this.deps.now()), this.lastTimestamp + 1);
    this.lastTimestamp = timestamp;
    engine.segment(frame, timestamp, () => undefined);
  }

  /**
   * One compositing tick. The pump NEVER waits for inference: compositing runs
   * off the last fresh mask/pose every tick; a new pass runs only when the
   * adaptive gate ({@link INFERENCE_BACKOFF}) opens, and its result lands in the
   * same synchronous call (no queue). The raw frame is downscaled onto the
   * inference canvas once per tick that runs any engine.
   */
  private tick(): void {
    if (this.stopped) {
      return;
    }
    this.scheduleNext();
    const video = this.video;
    if (video === null || this.degraded) {
      return;
    }
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width === 0 || height === 0) {
      return;
    }
    this.tickCount += 1;
    if (width !== this.nativeWidth || height !== this.nativeHeight) {
      this.rebuildPipeline(width, height);
    }
    const engine = this.engine;
    if (engine === null) {
      return;
    }
    try {
      const now = this.deps.now();
      const segmentDue =
        this.mode !== 'none' &&
        now - this.segmentSettledAt >= this.lastSegmentMs * INFERENCE_BACKOFF;
      const landmarkActive = this.mask !== 'none' && this.landmarkEngine !== null;
      const landmarkDue =
        landmarkActive && now - this.landmarkSettledAt >= this.lastLandmarkMs * INFERENCE_BACKOFF;
      if (segmentDue || landmarkDue) {
        this.drawInferenceFrame(video);
      }
      const inferenceInput = this.inferenceCanvas;
      // A whole-frame filter needs no person mask: in the raw mode the tick
      // skips segmentation entirely (the composite cost is the filter pass only).
      if (segmentDue && inferenceInput !== null) {
        const startedAt = this.deps.now();
        const timestamp = Math.max(Math.floor(startedAt), this.lastTimestamp + 1);
        this.lastTimestamp = timestamp;
        engine.segment(inferenceInput, timestamp, (segmented) => {
          this.personMask = segmented;
          if (this.renderer === null) {
            // 2D path: rasterize + feather the mask onto its canvases now. The
            // GPU path uploads the mask inside the composite draw instead.
            this.paintMask(segmented);
            this.featherMask();
          }
        });
        this.segmentSettledAt = this.deps.now();
        this.lastSegmentMs = Math.max(0, this.segmentSettledAt - startedAt);
      }
      if (landmarkDue && inferenceInput !== null) {
        this.detectFace(inferenceInput);
      }
      this.render();
    } catch {
      // Runtime segmentation/landmark failure: end the processed output and tell
      // the owner to fall back to the raw camera track.
      this.degrade();
    }
  }

  /** Draws the raw frame onto the small inference canvas (once per engine tick). */
  private drawInferenceFrame(video: HTMLVideoElement): void {
    const canvas = this.inferenceCanvas;
    const ctx = this.inferenceCtx;
    if (canvas === null || ctx === null) {
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  }

  /**
   * Creates (or re-creates after a capture size change) the small inference input
   * canvas: fitted into {@link INFERENCE_LONG_SIDE} preserving aspect, never
   * upscaled, even-rounded - the same rule the processing canvas follows.
   */
  private ensureInferenceCanvas(
    nativeWidth: number,
    nativeHeight: number,
  ): HTMLCanvasElement | null {
    if (nativeWidth <= 0 || nativeHeight <= 0) {
      return this.inferenceCanvas;
    }
    const fitted = fittedSize(nativeWidth, nativeHeight, INFERENCE_LONG_SIDE, INFERENCE_LONG_SIDE);
    if (
      this.inferenceCanvas !== null &&
      this.inferenceCanvas.width === fitted.width &&
      this.inferenceCanvas.height === fitted.height
    ) {
      return this.inferenceCanvas;
    }
    const canvas = this.deps.createCanvas(fitted.width, fitted.height);
    this.inferenceCanvas = canvas;
    this.inferenceCtx = contextOf(canvas);
    return canvas;
  }

  /**
   * One landmark pass for the active mask, honoring the tier throttle ("low"
   * detects every other tick, the EMA pose covers the skipped frame) and the
   * adaptive pacing gate (the caller only routes the tick here when it is due).
   * No face: the pose stays as-is and the fade-out (faceMasks.maskAlpha) hides
   * the mask after 0.5 s. Degenerate landmark frames keep the last pose without
   * touching the fade clock.
   */
  private detectFace(frame: HTMLVideoElement | HTMLCanvasElement): void {
    const landmarkEngine = this.landmarkEngine;
    if (this.mask === 'none' || landmarkEngine === null || this.maskSprites === null) {
      return;
    }
    if (!shouldDetectLandmarks(this.tickCount, this.resolved)) {
      return;
    }
    const now = this.deps.now();
    const startedAt = now;
    const timestamp = Math.max(Math.floor(now), this.lastLandmarkTimestamp + 1);
    this.lastLandmarkTimestamp = timestamp;
    const landmarks = landmarkEngine.detect(frame, timestamp);
    this.landmarkSettledAt = this.deps.now();
    this.lastLandmarkMs = Math.max(0, this.landmarkSettledAt - startedAt);
    if (landmarks === null) {
      return;
    }
    const pose = facePoseFromLandmarks(landmarks, this.width, this.height);
    if (pose === null) {
      return;
    }
    this.smoothedFace = smoothFace(this.smoothedFace, pose, landmarkSmoothingAlpha(this.resolved));
    this.lastFaceAt = now;
  }

  private scheduleNext(): void {
    const video = this.video;
    if (video === null) {
      return;
    }
    this.cancelFrame?.();
    this.cancelFrame = this.deps.scheduleFrame(video, () => this.tick());
  }

  /**
   * Composites one output frame: background layer, then the sharp person layer
   * masked by the feathered segmentation mask. Until the first mask arrives the
   * raw frame passes through unchanged (no person-invisible flash). The face
   * mask draws on top of the finished composite (pose-gated: nothing before the
   * first detection, faded once no face is seen for a while), and a whole-frame
   * color filter applies as the final pass over everything (raw mode included -
   * the raw+filter combination costs this composite pass, documented).
   *
   * The composite stage itself runs on the GPU renderer when the rebuild could
   * create one (sharp subject + full-res shader blur), otherwise on the 2D
   * canvases. The whole method is timed for the dev/e2e perf bridge.
   */
  private render(): void {
    const output = this.outputCtx;
    const outputCanvas = this.outputCanvas;
    const video = this.video;
    const params = this.params;
    if (output === null || outputCanvas === null || video === null || params === null) {
      return;
    }
    const startedAt = this.deps.now();
    const renderer = this.renderer;
    if (renderer !== null) {
      const background: CompositeBackground =
        this.mode === 'office'
          ? { kind: 'image', image: this.ensureOfficeCanvas() }
          : { kind: 'blur' };
      renderer.render({
        source: video,
        background,
        blurRadiusPx: blurRadiusPx(params.blurPx, this.blurStrength),
        featherPx: params.featherPx,
        mask: this.mode === 'none' ? null : this.personMask,
        blendWithPrevious: params.temporalSmoothing,
      });
      output.drawImage(renderer.canvas, 0, 0, this.width, this.height);
    } else if (this.mode === 'none' || this.personMask === null) {
      output.drawImage(video, 0, 0, this.width, this.height);
    } else {
      if (this.mode === 'office') {
        output.drawImage(this.ensureOfficeCanvas(), 0, 0, this.width, this.height);
      } else {
        // The background copy is blurred at QUARTER resolution and upscaled: the
        // ctx.filter pass (the expensive one) runs on 1/16 of the pixels and the
        // softened upscale spreads like a full-res blur of the scaled-up radius.
        const bg = this.bgCtx;
        const bgCanvas = this.bgCanvas;
        if (bg === null || bgCanvas === null) {
          return;
        }
        output.drawImage(video, 0, 0, this.width, this.height);
        bg.filter = blurFilter(blurRadiusPx(params.blurPx, this.blurStrength) / FEATHER_DOWNSCALE);
        bg.drawImage(video, 0, 0, bgCanvas.width, bgCanvas.height);
        bg.filter = 'none';
        output.drawImage(bgCanvas, 0, 0, this.width, this.height);
      }
      const person = this.personCtx;
      const personCanvas = this.personCanvas;
      const featheredCanvas = this.featheredCanvas;
      if (person === null || personCanvas === null || featheredCanvas === null) {
        return;
      }
      person.drawImage(video, 0, 0, this.width, this.height);
      person.globalCompositeOperation = 'destination-in';
      person.drawImage(featheredCanvas, 0, 0);
      person.globalCompositeOperation = 'source-over';
      output.drawImage(personCanvas, 0, 0);
    }
    this.drawFaceMask(output);
    this.applyWholeFrameFilter(output, outputCanvas);
    this.publishFrame();
    this.recordCompositeMs(this.deps.now() - startedAt);
  }

  /** The office backdrop canvas, generated once per pipeline (both backends). */
  private ensureOfficeCanvas(): HTMLCanvasElement {
    if (this.officeCanvas === null) {
      this.officeCanvas = this.deps.drawOfficeBackdrop(this.width, this.height);
    }
    return this.officeCanvas;
  }

  /** EMA of the composite stage cost (the dev/e2e perf bridge reads this). */
  private recordCompositeMs(sampleMs: number): void {
    this.compositeFrames += 1;
    this.compositeMsAvg =
      this.compositeFrames === 1 ? sampleMs : this.compositeMsAvg * 0.9 + sampleMs * 0.1;
  }

  /** The dev/e2e perf snapshot of the composite stage (see {@link exposeFxPerfForTests}). */
  private perfSnapshot(): FxRenderPerfSnapshot {
    return {
      backend: this.renderer === null ? 'canvas2d' : this.renderer.backend,
      compositeMsAvg: Math.round(this.compositeMsAvg * 100) / 100,
      frames: this.compositeFrames,
      width: this.publishCanvas?.width ?? 0,
      height: this.publishCanvas?.height ?? 0,
    };
  }

  /**
   * Publishes the finished composite: one blit of the tier-sized processing
   * canvas onto the dimension-stable canvas the output track captures (the blit
   * scales the processed frame to the published size - see the module header
   * for why that size never changes on an effects swap).
   */
  private publishFrame(): void {
    const publishCtx = this.publishCtx;
    const publishCanvas = this.publishCanvas;
    const outputCanvas = this.outputCanvas;
    if (publishCtx === null || publishCanvas === null || outputCanvas === null) {
      return;
    }
    publishCtx.drawImage(outputCanvas, 0, 0, publishCanvas.width, publishCanvas.height);
  }

  /**
   * The face-mask pass: anchors the sprite on the smoothed pose (translate to
   * the anchor, rotate by the eye-line roll, scale by the eye distance against
   * the canonical 100-unit geometry). Skipped entirely before the first face
   * detection and once the fade-out completed (no per-frame cost without a face).
   */
  private drawFaceMask(output: CanvasRenderingContext2D): void {
    const face = this.smoothedFace;
    const sprites = this.maskSprites;
    if (face === null || sprites === null || this.mask === 'none') {
      return;
    }
    const sprite = sprites[this.mask];
    const alpha = maskAlpha(this.deps.now(), this.lastFaceAt);
    if (sprite === undefined || alpha <= 0) {
      return;
    }
    const definition = maskDefinitionOf(this.mask);
    const anchor = definition.anchor(face);
    const scale = face.eyeDistance / CANONICAL_EYE_DISTANCE;
    output.save();
    output.globalAlpha = alpha;
    output.translate(anchor.x, anchor.y);
    output.rotate(face.roll);
    output.scale(scale, scale);
    // The transform puts us in canonical units: the sprite offset is canonical
    // (anchor distance from the sprite's top-left), the natural sprite size
    // divides by its pixel ratio (sprite px per canonical unit).
    output.drawImage(
      sprite.canvas,
      -sprite.originX,
      -sprite.originY,
      sprite.canvas.width / sprite.pixelRatio,
      sprite.canvas.height / sprite.pixelRatio,
    );
    output.restore();
  }

  /**
   * The whole-frame color filter pass: the finished composite snapshot-draws
   * onto itself through `ctx.filter` (a canvas self-draw reads the pre-draw
   * content), so person and background are tinted together. "none" skips the
   * pass entirely - frames pay nothing when no filter is chosen.
   */
  private applyWholeFrameFilter(
    output: CanvasRenderingContext2D,
    outputCanvas: HTMLCanvasElement,
  ): void {
    const css = VIDEO_FILTER_CSS[this.filter];
    if (css === 'none') {
      return;
    }
    output.filter = css;
    output.drawImage(outputCanvas, 0, 0);
    output.filter = 'none';
  }

  /** Repaints the mask canvas (white pixels, alpha = person) from a segment result. */
  private paintMask(mask: PersonMask): void {
    if (this.maskCanvas === null || this.maskCtx === null) {
      return;
    }
    if (this.maskCanvas.width !== mask.width || this.maskCanvas.height !== mask.height) {
      this.maskCanvas.width = mask.width;
      this.maskCanvas.height = mask.height;
    }
    // The RGBA staging buffer is reused across passes (the model output size is
    // fixed per session) - one allocation, not one per frame.
    const byteLength = mask.width * mask.height * 4;
    let rgba = this.maskRgba;
    if (rgba === null || rgba.length !== byteLength) {
      rgba = new Uint8ClampedArray(byteLength);
      this.maskRgba = rgba;
    }
    for (let i = 0; i < mask.width * mask.height; i += 1) {
      // PersonMask is already person-alpha (255 = person); copy it straight in.
      const person = mask.data[i] ?? 0;
      rgba[i * 4] = PERSON_MASK_VALUE;
      rgba[i * 4 + 1] = PERSON_MASK_VALUE;
      rgba[i * 4 + 2] = PERSON_MASK_VALUE;
      rgba[i * 4 + 3] = person;
    }
    this.maskCtx.putImageData(this.deps.createImageData(rgba, mask.width, mask.height), 0, 0);
  }

  /**
   * The feather pass: softens the raw mask into the canvas the person layer
   * consumes. The mask is drawn into a quarter-size canvas with a blur filter
   * (radius scaled down by {@link FEATHER_DOWNSCALE}, so the upscale restores
   * the full feather radius), then upscaled to the output size; at the "high"
   * level the previous frame's softened mask blends in at 50/50 to damp edge
   * flicker. Runs once per fresh mask, not per compositing tick.
   */
  private featherMask(): void {
    const params = this.params;
    const maskCanvas = this.maskCanvas;
    const featherCanvas = this.featherCanvas;
    const featherCtx = this.featherCtx;
    const featheredCanvas = this.featheredCanvas;
    const featheredCtx = this.featheredCtx;
    if (
      params === null ||
      maskCanvas === null ||
      featherCanvas === null ||
      featherCtx === null ||
      featheredCanvas === null ||
      featheredCtx === null
    ) {
      return;
    }
    featherCtx.clearRect(0, 0, featherCanvas.width, featherCanvas.height);
    featherCtx.filter = blurFilter(params.featherPx / FEATHER_DOWNSCALE);
    featherCtx.drawImage(maskCanvas, 0, 0, featherCanvas.width, featherCanvas.height);
    featherCtx.filter = 'none';

    featheredCtx.clearRect(0, 0, featheredCanvas.width, featheredCanvas.height);
    if (params.temporalSmoothing && this.prevMaskReady && this.prevFeatherCanvas !== null) {
      // Previous mask at full alpha, current at half: source-over blends the
      // alphas exactly 50/50, smoothing the boundary between the two frames.
      featheredCtx.drawImage(
        this.prevFeatherCanvas,
        0,
        0,
        featheredCanvas.width,
        featheredCanvas.height,
      );
      featheredCtx.globalAlpha = 0.5;
      featheredCtx.drawImage(featherCanvas, 0, 0, featheredCanvas.width, featheredCanvas.height);
      featheredCtx.globalAlpha = 1;
    } else {
      featheredCtx.drawImage(featherCanvas, 0, 0, featheredCanvas.width, featheredCanvas.height);
    }

    const prevFeatherCanvas = this.prevFeatherCanvas;
    const prevFeatherCtx = this.prevFeatherCtx;
    if (params.temporalSmoothing && prevFeatherCanvas !== null && prevFeatherCtx !== null) {
      prevFeatherCtx.clearRect(0, 0, prevFeatherCanvas.width, prevFeatherCanvas.height);
      prevFeatherCtx.drawImage(
        featherCanvas,
        0,
        0,
        prevFeatherCanvas.width,
        prevFeatherCanvas.height,
      );
      this.prevMaskReady = true;
    }
  }

  /** (Re)creates the canvases and output track for a native frame size. */
  private rebuildPipeline(nativeWidth: number, nativeHeight: number): void {
    const params = this.params;
    if (params === null) {
      throw new Error('pipeline rebuild before the quality level resolved');
    }
    this.nativeWidth = nativeWidth;
    this.nativeHeight = nativeHeight;
    const fitted = fittedSize(nativeWidth, nativeHeight, params.maxWidth, params.maxHeight);
    this.width = fitted.width;
    this.height = fitted.height;
    this.officeCanvas = null;
    this.prevMaskReady = false;
    // The inference input follows the capture's aspect (idempotent at a size match).
    this.ensureInferenceCanvas(nativeWidth, nativeHeight);
    // GPU composite first: the WebGL2 renderer replaces the whole blur+feather
    // canvas set (sharp subject, full-res shader blur). null - no WebGL2, or the
    // GPU pipeline failed to initialize - keeps the 2D path below.
    const gpu = this.deps.createCompositeRenderer(fitted.width, fitted.height);
    this.renderer?.dispose();
    this.renderer = gpu;
    const smallWidth = Math.max(1, Math.floor(fitted.width / FEATHER_DOWNSCALE));
    const smallHeight = Math.max(1, Math.floor(fitted.height / FEATHER_DOWNSCALE));
    const bgCanvas = gpu === null ? this.deps.createCanvas(smallWidth, smallHeight) : null;
    const outputCanvas = this.deps.createCanvas(fitted.width, fitted.height);
    const personCanvas = gpu === null ? this.deps.createCanvas(fitted.width, fitted.height) : null;
    const maskCanvas = gpu === null ? this.deps.createCanvas(1, 1) : null;
    const featherCanvas = gpu === null ? this.deps.createCanvas(smallWidth, smallHeight) : null;
    const featheredCanvas =
      gpu === null ? this.deps.createCanvas(fitted.width, fitted.height) : null;
    const prevFeatherCanvas =
      gpu === null && params.temporalSmoothing
        ? this.deps.createCanvas(smallWidth, smallHeight)
        : null;
    const bgCtx = bgCanvas === null ? null : contextOf(bgCanvas);
    const outputCtx = contextOf(outputCanvas);
    const personCtx = personCanvas === null ? null : contextOf(personCanvas);
    const maskCtx = maskCanvas === null ? null : contextOf(maskCanvas);
    const featherCtx = featherCanvas === null ? null : contextOf(featherCanvas);
    const featheredCtx = featheredCanvas === null ? null : contextOf(featheredCanvas);
    const prevFeatherCtx = prevFeatherCanvas === null ? null : contextOf(prevFeatherCanvas);
    // The published canvas follows the PER-SESSION publish policy (see
    // publishSize): min(capture dims, tier publish cap), chosen at the session's
    // first rebuild. A later rebuild (the capture changed dimensions mid-run)
    // KEEPS the current publish canvas AND its captured track whenever the
    // policy still returns their size: a fresh captureStream track for an
    // unchanged published size would itself reach the sender as a track
    // replacement. Only a published size that no longer fits the new capture
    // recreates the canvas and swaps the track inside the same stream so a
    // <video> bound to it keeps rendering.
    const currentPublish =
      this.publishCanvas === null
        ? undefined
        : { width: this.publishCanvas.width, height: this.publishCanvas.height };
    const published = publishSize(nativeWidth, nativeHeight, this.publishCap, currentPublish);
    const publishKept =
      this.publishCanvas !== null &&
      this.publishCanvas.width === published.width &&
      this.publishCanvas.height === published.height;
    if (!publishKept) {
      const publishCanvas = this.deps.createCanvas(published.width, published.height);
      const publishCtx = contextOf(publishCanvas);
      const track = this.deps.captureStream(publishCanvas, params.fps);
      if (this.outputStream === null) {
        this.outputStream = this.deps.createStream(track);
      } else {
        // Published size changed mid-run: swap the track inside the same stream
        // so a <video> bound to it keeps rendering.
        for (const old of this.outputStream.getVideoTracks()) {
          this.outputStream.removeTrack(old);
          old.stop();
        }
        this.outputStream.addTrack(track);
      }
      this.outputTrack?.stop();
      this.outputTrack = track;
      this.publishCanvas = publishCanvas;
      this.publishCtx = publishCtx;
    }
    this.outputCtx = outputCtx;
    this.outputCanvas = outputCanvas;
    this.bgCanvas = bgCanvas;
    this.bgCtx = bgCtx;
    this.personCanvas = personCanvas;
    this.personCtx = personCtx;
    this.maskCanvas = maskCanvas;
    this.maskCtx = maskCtx;
    this.featherCanvas = featherCanvas;
    this.featherCtx = featherCtx;
    this.featheredCanvas = featheredCanvas;
    this.featheredCtx = featheredCtx;
    this.prevFeatherCanvas = prevFeatherCanvas;
    this.prevFeatherCtx = prevFeatherCtx;
  }

  private degrade(): void {
    this.degraded = true;
    this.teardown();
    this.onFallback?.();
  }

  private teardown(): void {
    if (this.stopped) {
      return;
    }
    this.stopped = true;
    this.cancelFrame?.();
    this.cancelFrame = null;
    this.engine?.close();
    this.engine = null;
    this.landmarkEngine?.close();
    this.landmarkEngine = null;
    this.maskSprites = null;
    this.smoothedFace = null;
    if (this.video !== null) {
      this.video.pause();
      this.video.srcObject = null;
      this.video = null;
    }
    this.outputTrack?.stop();
    this.outputTrack = null;
    this.outputCtx = null;
    this.outputCanvas = null;
    this.renderer?.dispose();
    this.renderer = null;
    this.compositeMsAvg = 0;
    this.compositeFrames = 0;
    this.bgCtx = null;
    this.bgCanvas = null;
    this.inferenceCtx = null;
    this.inferenceCanvas = null;
    this.maskRgba = null;
    this.personCtx = null;
    this.personCanvas = null;
    this.maskCtx = null;
    this.maskCanvas = null;
    this.featherCtx = null;
    this.featherCanvas = null;
    this.featheredCtx = null;
    this.featheredCanvas = null;
    this.prevFeatherCtx = null;
    this.prevFeatherCanvas = null;
    this.officeCanvas = null;
    this.outputStream = null;
  }

  private waitForVideoReady(video: HTMLVideoElement): Promise<void> {
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('camera video did not deliver its first frame in time'));
      }, VIDEO_READY_TIMEOUT_MS);
      const onReady = (): void => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          cleanup();
          resolve();
        }
      };
      const cleanup = (): void => {
        clearTimeout(timer);
        video.removeEventListener('loadedmetadata', onReady);
        video.removeEventListener('resize', onReady);
      };
      video.addEventListener('loadedmetadata', onReady);
      video.addEventListener('resize', onReady);
    });
  }
}

function contextOf(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('canvas 2d context is unavailable');
  }
  return ctx;
}

function playVideo(video: HTMLVideoElement): void {
  try {
    void video.play().catch(() => undefined);
  } catch {
    // jsdom and exotic embedders may throw synchronously; the ready wait handles frames.
  }
}

/** Lazily resolves the production browser deps (keeps @mediapipe out of the jsdom graph). */
async function loadBrowserDeps(): Promise<ProcessorDeps> {
  const module = await import('./segmentationEngine');
  return module.createBrowserProcessorDeps();
}

/** Module-level reader of the live processor's perf snapshot (the dev bridge state). */
let devPerfReader: (() => FxRenderPerfSnapshot | null) | null = null;

/**
 * Dev/e2e seam: exposes the composite stage's perf snapshot to Playwright page
 * evaluation (`window.__vcFxPerf()`) - backend, composite ms/frame EMA, frame
 * count and published size; null while no processed pipeline runs. Stripped
 * from production builds by the import.meta.env.DEV gate, like the camera-fx
 * bridge in room/cameraEffects.ts.
 */
export function exposeFxPerfForTests(): void {
  if (import.meta.env.DEV !== true) {
    return;
  }
  window.__vcFxPerf = (): FxRenderPerfSnapshot | null => devPerfReader?.() ?? null;
}

declare global {
  interface Window {
    /** Dev/e2e only: the effects composite stage's perf snapshot (null when inactive). */
    __vcFxPerf?: () => FxRenderPerfSnapshot | null;
  }
}
